// Data-driven SEO landing pages dataset.
// Each entry powers a static SEO route (rendered via <SeoLanding/>) AND the
// build-time static shell generator (scripts/generate-seo-static.ts) so that
// production curl/source contains crawlable per-route metadata + body text.

export type SeoFaq = { q: string; a: string };

export type SeoPage = {
  slug: string;            // URL path WITHOUT leading slash
  title: string;           // <title>
  description: string;     // meta description
  h1: string;              // visible H1
  kicker?: string;         // small label above H1
  lede: string;            // hero paragraph
  primaryKeyword: string;  // analytics + JSON-LD keyword
  audience?: string;       // who this is for
  useCases: { title: string; body: string }[];
  comparison?: { headline: string; rows: { label: string; eva: string; them: string }[] };
  faqs: SeoFaq[];
  related: string[];       // related slugs
  ctaLabel?: string;
  ctaPlan?: "solo" | "studio" | "cobrain";
};

const HUB_USE_CASES: SeoPage["useCases"] = [
  { title: "Sends invoices & chases them", body: "Eva drafts, sends and follows up on invoices until they're paid. No more 60-day receivables." },
  { title: "Sources & qualifies leads", body: "She researches prospects, finds contact info, and writes the first outreach in your voice." },
  { title: "Runs outreach campaigns", body: "Multi-step email/LinkedIn sequences with reply detection — she hands warm replies back to you." },
  { title: "Answers clients 24/7", body: "WhatsApp, iMessage, SMS, email and voice. She remembers every client and every promise." },
  { title: "Designs sites & assets", body: "Landing pages, proposals, one-pagers — drafted overnight, on brand, ready to ship." },
  { title: "Builds & runs workflows", body: "She wires your tools together — CRM, calendar, accounting — and keeps them clean." },
];

const HUB_FAQS: SeoFaq[] = [
  { q: "Is Eva just an AI receptionist?", a: "No. An AI receptionist only answers the phone. Eva is an AI operations manager — she sends invoices, sources leads, runs outreach, designs sites, handles messages, builds workflows and remembers your business. Reception is one narrow use case." },
  { q: "How much does Eva cost?", a: "From $1,500/month, scoped — deployed by your own engineer. A comparable human ops manager runs $5,000–$20,000/month. No per-seat games, no per-minute call billing." },
  { q: "How fast can Eva start?", a: "24-hour setup. We onboard her on your tools (CRM, calendar, inbox, accounting) and she's working by tomorrow." },
  { q: "Will Eva replace my team?", a: "No. Eva replaces the work nobody on your team wants to do at 11pm — chasing invoices, qualifying leads, formatting proposals, updating records. Your people stay senior." },
  { q: "Where does Eva work?", a: "Inside the tools you already use — WhatsApp, iMessage, SMS, Slack, email, your CRM, your calendar, your accounting. No new dashboard your team has to learn." },
];

const COMPARE_HUB: SeoPage["comparison"] = {
  headline: "Eva vs. the alternatives",
  rows: [
    { label: "Cost / month",      eva: "From $1,500, engineer included",                 them: "$5k–$20k human · $499+ per agent stack" },
    { label: "Setup time",        eva: "24 hours",                  them: "30–90 days hiring or integrating" },
    { label: "Scope",             eva: "Invoices, leads, outreach, sites, ops", them: "One job each (just calls, just email, just CRM)" },
    { label: "Memory",            eva: "Remembers every client forever", them: "Resets each conversation / shift" },
    { label: "Coverage",          eva: "24/7, every channel",       them: "9–5, one channel" },
  ],
};

function p(partial: Omit<SeoPage, "useCases" | "faqs" | "comparison" | "related"> & Partial<Pick<SeoPage, "useCases" | "faqs" | "comparison" | "related">>): SeoPage {
  return {
    useCases: HUB_USE_CASES,
    faqs: HUB_FAQS,
    comparison: COMPARE_HUB,
    related: [],
    ...partial,
  };
}

export const SEO_PAGES: SeoPage[] = [
  p({
    slug: "ai-operations-manager-small-business",
    title: "AI Operations Manager for Small Businesses | HireEva",
    description: "Eva is an AI operations manager for small businesses — deployed by your own engineer. Not a chatbot. An AI operator for your business.",
    h1: "An AI operations manager for your small business.",
    kicker: "AI OPERATIONS MANAGER",
    lede: "An AI operator, deployed by your own engineer. Eva sends invoices, sources leads, runs outreach, designs sites, handles messages, builds workflows and remembers everything about your business.",
    primaryKeyword: "ai operations manager small business",
    audience: "Small business owners, founders and operators tired of duct-taping virtual assistants, chatbots and Zapier together.",
    related: ["ai-employee-small-business", "ai-admin-assistant-small-business", "ai-sales-assistant-small-business", "ai-appointment-scheduling-assistant", "ai-estimate-follow-up-assistant", "ai-automation-tools-alternative", "ai-virtual-assistant-small-business", "ai-workflow-automation-small-business", "hireeva-vs-virtual-assistant", "texas-ai-operations-manager", "hireeva-vs-ai-receptionist"],
  }),
  p({
    slug: "ai-virtual-assistant-small-business",
    title: "AI Virtual Assistant for Small Business | HireEva",
    description: "Eva is an AI virtual assistant for small businesses. Sends invoices, books jobs, replies to clients across SMS, WhatsApp, email and voice. from $1,500/month.",
    h1: "An AI virtual assistant that actually does the work.",
    lede: "Most virtual assistants need managing. Eva manages herself. She runs invoices, leads, outreach and client replies in the background — deployed by an engineer who knows your business.",
    primaryKeyword: "ai virtual assistant small business",
    related: ["ai-operations-manager-small-business", "hireeva-vs-virtual-assistant", "ai-executive-assistant-small-business", "ai-customer-follow-up-assistant"],
  }),
  p({
    slug: "ai-executive-assistant-small-business",
    title: "AI Executive Assistant for Founders & Operators | HireEva",
    description: "An AI executive assistant that drafts emails, books meetings, follows up on invoices and chases warm leads. from $1,500/month — replaces a $20k/month chief of staff.",
    h1: "An AI executive assistant for founders & operators.",
    lede: "Eva is the assistant you'd hire if you could afford a $20,000/month chief of staff. She drafts your inbox, prepares your meetings, runs follow-ups and never forgets a contact.",
    primaryKeyword: "ai executive assistant small business",
    related: ["ai-operations-manager-small-business", "ai-office-manager-small-business", "ai-customer-follow-up-assistant"],
  }),
  p({
    slug: "ai-office-manager-small-business",
    title: "AI Office Manager for Small Businesses | HireEva",
    description: "Eva runs your back office: invoices out, follow-ups in, vendors paid, calendars clean, clients answered. from $1,500/month.",
    h1: "An AI office manager that keeps the wheels turning.",
    lede: "Stop paying a part-time office manager $3,500/month to copy things between tools. Eva runs the back office end-to-end and remembers every detail.",
    primaryKeyword: "ai office manager small business",
    related: ["ai-operations-manager-small-business", "hireeva-vs-office-manager", "ai-invoice-follow-up-automation"],
  }),
  p({
    slug: "ai-employee-small-business",
    title: "AI Employee for Small Businesses — from $1,500/month | HireEva",
    description: "An AI employee that takes action — not a chatbot. Sends invoices, sources leads, runs outreach, builds workflows, updates systems. from $1,500/month.",
    h1: "Hire an AI employee. Not another chatbot.",
    lede: "Eva is an AI employee for your small business. She has memory, takes action, uses your tools, and ships work overnight. From $1,500/month, she replaces $20,000/month in human ops cost.",
    primaryKeyword: "ai employee small business",
    related: ["ai-operations-manager-small-business", "ai-virtual-assistant-small-business", "hireeva-vs-chatgpt"],
  }),
  p({
    slug: "ai-marketing-assistant-small-business",
    title: "AI Marketing Assistant for Small Businesses | HireEva",
    description: "Eva drafts your newsletter, writes outreach, designs landing pages and runs follow-ups. An AI marketing assistant from $1,500/month.",
    h1: "An AI marketing assistant who actually ships.",
    lede: "Most marketing assistants need a brief. Eva writes the brief, drafts the copy, designs the page, sends the outreach and reports the result.",
    primaryKeyword: "ai marketing assistant small business",
    related: ["ai-operations-manager-small-business", "ai-outreach-assistant-small-business", "ai-website-design-assistant", "ai-lead-sourcing-assistant"],
  }),
  p({
    slug: "ai-lead-sourcing-assistant",
    title: "AI Lead Sourcing Assistant — Find & Qualify Leads | HireEva",
    description: "Eva researches prospects, finds contact info, and qualifies leads before they hit your inbox. An AI lead sourcing assistant from $1,500/month.",
    h1: "An AI lead sourcing assistant that fills the pipeline.",
    lede: "Eva searches the web, scrapes signal, finds decision-makers and writes the first outreach in your voice. You wake up to a calendar with qualified calls.",
    primaryKeyword: "ai lead sourcing assistant",
    related: ["ai-outreach-assistant-small-business", "ai-marketing-assistant-small-business", "ai-operations-manager-small-business"],
  }),
  p({
    slug: "ai-invoice-follow-up-automation",
    title: "AI Invoice Follow-Up Automation | HireEva",
    description: "Eva drafts, sends and chases invoices until they're paid. AI invoice follow-up automation from $1,500/month — collect 30 days faster.",
    h1: "AI invoice follow-up automation that gets you paid.",
    lede: "60-day receivables become 20-day receivables. Eva sends the invoice, follows up politely, escalates when needed, and books it into your accounting.",
    primaryKeyword: "ai invoice follow up automation",
    related: ["ai-office-manager-small-business", "ai-customer-follow-up-assistant", "ai-operations-manager-small-business"],
  }),
  p({
    slug: "ai-website-design-assistant",
    title: "AI Website Design Assistant for Small Businesses | HireEva",
    description: "Eva drafts landing pages, proposals and one-pagers overnight, on brand and ready to ship. AI website design assistant from $1,500/month.",
    h1: "An AI website design assistant on call.",
    lede: "Need a landing page by tomorrow? Eva writes the copy, picks the layout, drops in the imagery and ships you a ready-to-launch site overnight.",
    primaryKeyword: "ai website design assistant",
    related: ["ai-marketing-assistant-small-business", "ai-operations-manager-small-business"],
  }),
  p({
    slug: "ai-workflow-automation-small-business",
    title: "AI Workflow Automation for Small Businesses | HireEva",
    description: "Eva builds and runs the workflows that keep your business clean — CRM updates, intake, follow-up, reporting. from $1,500/month, no Zapier sprawl.",
    h1: "AI workflow automation that replaces your Zapier graveyard.",
    lede: "Forget 47 broken Zaps. Eva understands your tools, runs the flows end-to-end and fixes herself when something breaks.",
    primaryKeyword: "ai workflow automation small business",
    related: ["ai-operations-manager-small-business", "ai-employee-small-business", "hireeva-vs-ai-receptionist"],
  }),
  p({
    slug: "ai-receptionist-contractors",
    title: "AI Receptionist for Contractors | HireEva",
    description: "Eva is more than an AI receptionist — she books jobs, sends quotes, chases invoices and replies on SMS, WhatsApp and voice. from $1,500/month.",
    h1: "An AI receptionist for contractors — and everything after the call.",
    lede: "Most AI receptionists just take a message. Eva books the job, sends the quote, calendars the crew, follows up after, and chases the invoice.",
    primaryKeyword: "ai receptionist contractors",
    audience: "Plumbers, electricians, HVAC, roofers, painters, landscapers and trade businesses.",
    related: ["ai-operations-manager-for-contractors", "ai-operations-manager-for-local-services", "ai-invoice-follow-up-automation", "hireeva-vs-ai-receptionist"],
  }),
  p({
    slug: "ai-operations-manager-for-agencies",
    title: "AI Operations Manager for Agencies | HireEva",
    description: "Eva runs ops for agencies — client comms, status updates, invoicing, follow-up — without adding headcount. from $1,500/month.",
    h1: "An AI operations manager for your agency.",
    lede: "Agencies live on margin. Eva protects it. She keeps clients answered, statuses updated, invoices out and follow-ups landing.",
    primaryKeyword: "ai operations manager agencies",
    related: ["ai-operations-manager-small-business", "ai-operations-manager-for-consultants"],
  }),
  p({
    slug: "ai-operations-manager-for-consultants",
    title: "AI Operations Manager for Consultants | HireEva",
    description: "Eva runs the back-office for consultants: invoicing, scheduling, client follow-up, proposal drafts. from $1,500/month.",
    h1: "An AI operations manager for independent consultants.",
    lede: "You bill by the hour. Stop spending those hours on admin. Eva runs invoicing, scheduling, follow-up and proposal drafts so you stay in the work.",
    primaryKeyword: "ai operations manager consultants",
    related: ["ai-operations-manager-small-business", "ai-executive-assistant-small-business"],
  }),
  p({
    slug: "ai-operations-manager-for-contractors",
    title: "AI Operations Manager for Contractors | HireEva",
    description: "Eva books jobs, sends quotes, chases invoices and answers clients 24/7 — for contractors. from $1,500/month.",
    h1: "An AI operations manager for contractors.",
    lede: "Trades businesses lose money to missed calls and slow invoicing. Eva books the job, sends the quote, and chases payment — all while you're on site.",
    primaryKeyword: "ai operations manager contractors",
    related: ["ai-receptionist-contractors", "ai-operations-manager-for-local-services", "hireeva-vs-ai-receptionist"],
  }),
  p({
    slug: "ai-operations-manager-for-clinics",
    title: "AI Operations Manager for Clinics & Practices | HireEva",
    description: "Eva books appointments, reminds patients, follows up after visits and chases unpaid balances. For clinics, from $1,500/month.",
    h1: "An AI operations manager for clinics & practices.",
    lede: "Reception is the small part. Eva books, reminds, follows up, recovers no-shows and chases balances — so your front desk can focus on patients.",
    primaryKeyword: "ai operations manager clinics",
    related: ["ai-operations-manager-small-business", "ai-customer-follow-up-assistant"],
  }),
  p({
    slug: "ai-operations-manager-for-local-services",
    title: "AI Operations Manager for Local Service Businesses | HireEva",
    description: "Eva runs ops for local service businesses: booking, quoting, follow-up, invoicing. from $1,500/month.",
    h1: "An AI operations manager for local service businesses.",
    lede: "Cleaners, movers, salons, studios. Eva books, quotes, reminds, follows up and chases — across SMS, WhatsApp and voice.",
    primaryKeyword: "ai operations manager local services",
    related: ["ai-receptionist-contractors", "ai-operations-manager-for-contractors"],
  }),
  p({
    slug: "austin-ai-operations-manager",
    title: "AI Operations Manager in Austin, TX | HireEva",
    description: "Austin small businesses use Eva as an AI operations manager from $1,500/month — invoices, leads, outreach, client comms. 24-hr setup.",
    h1: "AI operations manager for Austin small businesses.",
    lede: "From South Congress studios to East Austin trades crews — Eva is the AI ops manager Austin owners hire when they can't yet afford a chief of staff.",
    primaryKeyword: "austin ai operations manager",
    related: ["ai-operations-manager-small-business", "ai-operations-manager-for-local-services"],
  }),
  p({
    slug: "hireeva-vs-virtual-assistant",
    title: "HireEva vs. a Virtual Assistant — Honest Comparison",
    description: "A human VA costs $2,000–$4,000/month, needs managing, and forgets. Eva starts at $1,500/month, runs herself, and remembers everything.",
    h1: "HireEva vs. a virtual assistant.",
    lede: "A virtual assistant is a person you manage. Eva is software that manages itself. Here's the honest comparison — including where humans still win.",
    primaryKeyword: "hireeva vs virtual assistant",
    related: ["ai-virtual-assistant-small-business", "ai-operations-manager-small-business"],
  }),
  p({
    slug: "hireeva-vs-office-manager",
    title: "HireEva vs. a Human Office Manager — Honest Comparison",
    description: "An office manager runs $4,000–$8,000/month plus benefits. Eva starts at $1,500/month plus your AI usage, and never takes vacation.",
    h1: "HireEva vs. a human office manager.",
    lede: "Office managers are great. They're also expensive, scarce, and unavailable at 11pm. Here's how Eva compares — and where you still want a human.",
    primaryKeyword: "hireeva vs office manager",
    related: ["ai-office-manager-small-business", "ai-operations-manager-small-business"],
  }),
  p({
    slug: "hireeva-vs-chatgpt",
    title: "HireEva vs. ChatGPT — Operator vs. Chatbot",
    description: "ChatGPT answers questions. Eva takes action — sends invoices, books jobs, runs outreach, updates your systems. from $1,500/month.",
    h1: "HireEva vs. ChatGPT.",
    lede: "ChatGPT is a brilliant chatbot. Eva is an AI operator — she has memory, uses your tools, takes action overnight and reports back.",
    primaryKeyword: "hireeva vs chatgpt",
    related: ["ai-employee-small-business", "ai-operations-manager-small-business"],
  }),
  p({
    slug: "hireeva-vs-ai-receptionist",
    title: "HireEva vs. AI Receptionist: Operator vs. Answering",
    description: "AI receptionists only answer the phone. Eva books the job, sends the quote, chases the invoice and remembers every client. from $1,500/month.",
    h1: "HireEva vs. an AI receptionist.",
    lede: "An AI receptionist answers calls. That's it. Eva does everything after the call — booking, quoting, invoicing, follow-up — and remembers every client.",
    primaryKeyword: "hireeva vs ai receptionist",
    related: ["ai-receptionist-contractors", "ai-operations-manager-small-business"],
  }),
  p({
    slug: "ai-outreach-assistant-small-business",
    title: "AI Outreach Assistant for Small Businesses | HireEva",
    description: "Eva runs cold and warm outreach sequences in your voice, detects replies, and hands warm leads back to you. from $1,500/month.",
    h1: "An AI outreach assistant who writes like you.",
    lede: "Eva researches the prospect, drafts the message in your voice, runs the follow-ups, and books the meeting straight into your calendar.",
    primaryKeyword: "ai outreach assistant small business",
    related: ["ai-lead-sourcing-assistant", "ai-marketing-assistant-small-business"],
  }),
  p({
    slug: "ai-customer-follow-up-assistant",
    title: "AI Customer Follow-Up Assistant | HireEva",
    description: "Never drop a customer thread again. Eva follows up on quotes, jobs, invoices and check-ins automatically. from $1,500/month.",
    h1: "An AI customer follow-up assistant that never forgets.",
    lede: "Most small businesses lose 30%+ of revenue to dropped follow-ups. Eva remembers every quote, every promise, every check-in — and chases them all.",
    primaryKeyword: "ai customer follow up assistant",
    related: ["ai-invoice-follow-up-automation", "ai-operations-manager-small-business", "hireeva-vs-ai-receptionist"],
  }),
  p({
    slug: "ai-automation-tools-alternative",
    title: "AI Automation Tools Alternative: One Operator | HireEva",
    description: "Tired of stitching Zapier, Make, n8n and an LLM together? Eva is the AI automation alternative — one operator that runs the whole back office, from $1,500/month.",
    h1: "The AI automation tools alternative — one operator, not a stack.",
    kicker: "AUTOMATION ALTERNATIVE",
    lede: "Most AI automation stacks are five tools duct-taped together. Eva is one AI operations manager who replaces the whole graveyard — and remembers what she did yesterday.",
    primaryKeyword: "ai automation tools alternative",
    audience: "Founders and operators who tried Zapier + Make + n8n + ChatGPT and ended up maintaining the automation instead of running the business.",
    related: ["ai-workflow-automation-small-business", "ai-operations-manager-small-business", "ai-employee-small-business", "hireeva-vs-chatgpt", "hireeva-vs-ai-receptionist"],
  }),
  p({
    slug: "ai-admin-assistant-small-business",
    title: "AI Admin Assistant for Small Businesses | HireEva",
    description: "Eva handles the admin work no one wants — inbox triage, scheduling, data entry, follow-ups, invoicing. An AI admin assistant from $1,500/month.",
    h1: "An AI admin assistant that clears the busywork.",
    lede: "Most owners spend 12+ hours a week on admin. Eva absorbs it — triaging the inbox, updating the CRM, sending reminders and chasing paperwork — from $1,500/month instead of $4,000.",
    primaryKeyword: "ai admin assistant small business",
    audience: "Owners and operators drowning in inbox, scheduling and paperwork they shouldn't be doing themselves.",
    related: ["ai-operations-manager-small-business", "ai-executive-assistant-small-business", "ai-office-manager-small-business", "ai-appointment-scheduling-assistant"],
  }),
  p({
    slug: "ai-sales-assistant-small-business",
    title: "AI Sales Assistant for Small Businesses | HireEva",
    description: "Eva sources leads, qualifies them, runs outreach in your voice, follows up on estimates and books the call. An AI sales assistant from $1,500/month.",
    h1: "An AI sales assistant that fills your pipeline.",
    lede: "Stop paying an SDR $6k/month to chase the same 50 leads. Eva sources, qualifies, writes the outreach, follows up and books warm calls into your calendar — overnight.",
    primaryKeyword: "ai sales assistant small business",
    audience: "Founders and small sales teams who need pipeline but can't justify another headcount yet.",
    related: ["ai-lead-sourcing-assistant", "ai-outreach-assistant-small-business", "ai-estimate-follow-up-assistant", "ai-operations-manager-small-business"],
  }),
  p({
    slug: "ai-appointment-scheduling-assistant",
    title: "AI Appointment Scheduling Assistant | HireEva",
    description: "Eva books, reschedules and reminds — across SMS, WhatsApp, email and voice. An AI appointment scheduling assistant from $1,500/month.",
    h1: "An AI appointment scheduling assistant that books while you sleep.",
    lede: "No more phone tag, no more no-shows. Eva offers slots, books straight into your calendar, sends reminders and handles reschedules — on every channel your clients actually use.",
    primaryKeyword: "ai appointment scheduling assistant",
    audience: "Clinics, salons, trades, consultants and service businesses where the calendar is the business.",
    related: ["ai-operations-manager-for-clinics", "ai-operations-manager-for-local-services", "ai-receptionist-contractors", "ai-admin-assistant-small-business"],
  }),
  p({
    slug: "ai-estimate-follow-up-assistant",
    title: "AI Estimate & Quote Follow-Up Assistant | HireEva",
    description: "Half of all estimates never get a follow-up. Eva chases every quote until you get a yes, a no, or a date. from $1,500/month.",
    h1: "An AI estimate follow-up assistant that closes the loop.",
    lede: "Most contractors and consultants lose 40%+ of estimates to silence. Eva follows up politely, escalates on time, and brings back yes/no/date answers — so you stop quoting into the void.",
    primaryKeyword: "ai estimate follow up assistant",
    audience: "Contractors, agencies and consultants whose pipeline dies in the follow-up gap after a quote goes out.",
    related: ["ai-invoice-follow-up-automation", "ai-customer-follow-up-assistant", "ai-sales-assistant-small-business", "ai-operations-manager-for-contractors", "hireeva-vs-ai-receptionist"],
  }),
  p({
    slug: "texas-ai-operations-manager",
    title: "AI Operations Manager in Texas | HireEva",
    description: "Texas small businesses use Eva as an AI operations manager from $1,500/month — invoices, leads, outreach, client comms across SMS, WhatsApp and voice. 24-hr setup.",
    h1: "AI operations manager for Texas small businesses.",
    lede: "From Austin studios to Houston trades crews to Dallas consultancies — Eva is the AI ops manager Texas owners hire when they can't yet afford a $20k/month chief of staff.",
    primaryKeyword: "texas ai operations manager",
    audience: "Small businesses across Austin, Houston, Dallas, San Antonio and the rest of Texas.",
    related: ["austin-ai-operations-manager", "ai-operations-manager-small-business", "ai-operations-manager-for-local-services", "ai-operations-manager-for-contractors"],
  }),
  p({
    slug: "ai-agent-software",
    title: "AI Agent Software for Small Businesses | HireEva",
    description: "Eva is AI agent software built for small businesses — not a developer framework like CrewAI or LangChain. One agent that sends invoices, sources leads and runs ops, from $1,500/month.",
    h1: "AI agent software, built for the people running the business.",
    kicker: "AI AGENT SOFTWARE",
    lede: "Most AI agent software is a developer framework you have to build. Eva is AI agent software you just hire — one operator that runs invoices, leads, outreach, scheduling and client comms, from $1,500/month.",
    primaryKeyword: "ai agent software",
    audience: "Small business owners and operators evaluating AI agents who don't want to wire together CrewAI, LangChain, Zapier and an LLM themselves.",
    useCases: [
      { title: "One agent, not a framework.", body: "CrewAI, LangChain and AutoGen are toolkits for engineers. Eva is a finished agent — hired in 24 hours, no Python required." },
      { title: "Takes action across your tools.", body: "Eva uses your CRM, calendar, inbox and accounting directly. She doesn't hand you a transcript — she does the work and reports back." },
      { title: "Persistent memory of your business.", body: "Every client, quote, promise and preference is remembered. No re-explaining context every session." },
      { title: "Multi-channel by default.", body: "WhatsApp, iMessage, SMS, email and voice — Eva meets clients where they already are, no chat widget required." },
      { title: "Ops work that ships overnight.", body: "Invoices sent, leads sourced, outreach drafted, follow-ups chased — work done by morning, not promised in a roadmap." },
      { title: "From $1,500/month, not per-seat.", body: "Predictable engagement pricing for a business tool, with no per-seat scaling. AI model and API usage is paid directly by you to your provider." },
    ],
    comparison: {
      headline: "Eva vs. other AI agent software",
      rows: [
        { label: "Who it's for",     eva: "Small business owners",        them: "Developers wiring frameworks together" },
        { label: "Setup",            eva: "24 hours, done-for-you",        them: "Weeks of engineering + ongoing maintenance" },
        { label: "Pricing",          eva: "From $1,500/month + your AI usage", them: "Per-token + per-seat + infra" },
        { label: "Memory",           eva: "Persistent across clients",     them: "Session-scoped or BYO vector DB" },
        { label: "Action surface",   eva: "Your real tools (CRM, inbox, calendar, accounting)", them: "Whatever you wire up" },
        { label: "Examples",         eva: "Eva — one operator",            them: "CrewAI, LangChain, AutoGen, Sierra, Relevance" },
      ],
    },
    faqs: [
      { q: "How is Eva different from CrewAI, LangChain or AutoGen?", a: "Those are developer frameworks — you build the agent. Eva is a finished AI agent you hire. No code, no orchestration layer. Onboarded in 24 hours, working by tomorrow." },
      { q: "Is Eva an AI agent or just an AI chatbot?", a: "An agent. A chatbot answers. Eva takes action — she sends the invoice, books the meeting, runs the outreach sequence and updates your CRM. She uses your tools directly, with memory across conversations." },
      { q: "What can Eva actually do?", a: "Send and chase invoices, source and qualify leads, run outreach campaigns, answer clients across SMS/WhatsApp/email/voice, book and remind appointments, draft landing pages, and update your back-office systems. One agent for the whole ops surface." },
      { q: "Do I need engineers to set Eva up?", a: "No. Setup is done-for-you in 24 hours. You connect your tools (calendar, inbox, CRM, accounting) and she's working the next day." },
      { q: "How much does AI agent software like Eva cost?", a: "Engagements start at $1,500/month, plus the AI model and API usage you pay your provider for directly. Developer frameworks are free to download but cost engineering time + LLM tokens + infra. Enterprise AI agent platforms typically start at $500–$2,000+/month per seat." },
    ],
    related: ["ai-operations-manager-small-business", "ai-employee-small-business", "ai-automation-tools-alternative", "hireeva-vs-chatgpt", "ai-workflow-automation-small-business"],
  }),
  // ===== Revenue-recovery pages =====
  p({
    slug: "ai-missed-call-follow-up-assistant",
    title: "AI Missed-Call Follow-Up Assistant | HireEva",
    description: "Every missed call is a lost job. Eva texts back missed callers within seconds, qualifies them, and books the work. from $1,500/month.",
    h1: "An AI missed-call follow-up assistant that recovers lost jobs.",
    kicker: "MISSED-CALL FOLLOW-UP",
    lede: "Small businesses miss 30–50% of inbound calls. Eva texts the caller back in seconds across SMS, WhatsApp and iMessage, answers their question, qualifies the job and books it — before the next competitor calls them back.",
    primaryKeyword: "ai missed call follow up assistant",
    audience: "Contractors, clinics, salons, agencies and local service businesses who lose revenue every time the phone rings unanswered.",
    related: ["ai-receptionist-contractors", "ai-front-desk-assistant-contractors", "ai-job-lead-follow-up-assistant", "ai-operations-manager-for-contractors", "ai-customer-follow-up-assistant"],
  }),
  p({
    slug: "ai-front-desk-assistant-contractors",
    title: "AI Front-Desk Assistant for Contractors | HireEva",
    description: "Eva runs the front desk for trades: answers calls, texts back missed callers, books jobs, sends quotes and chases invoices. from $1,500/month.",
    h1: "An AI front-desk assistant for contractors.",
    kicker: "AI FRONT DESK",
    lede: "You're on the roof, under the sink, or behind the wheel. Eva runs the front desk in your pocket — answers the phone, replies on SMS and WhatsApp, books the job, sends the quote and chases the invoice.",
    primaryKeyword: "ai front desk assistant contractors",
    audience: "Plumbers, electricians, HVAC, roofers, painters, landscapers and trades businesses who can't afford a $4k/month receptionist.",
    related: ["ai-receptionist-contractors", "ai-missed-call-follow-up-assistant", "ai-operations-manager-for-contractors", "ai-quote-follow-up-automation", "ai-job-lead-follow-up-assistant"],
  }),
  p({
    slug: "ai-quote-follow-up-automation",
    title: "AI Quote Follow-Up Automation | HireEva",
    description: "Eva chases every quote until you get a yes, a no, or a date. AI quote follow-up automation that closes the loop — from $1,500/month.",
    h1: "AI quote follow-up automation that turns silence into yes.",
    kicker: "QUOTE FOLLOW-UP",
    lede: "Most businesses send the quote and pray. Eva follows up on day 2, day 5 and day 10 in your voice — across email, SMS and WhatsApp — escalates when needed and brings back a real answer.",
    primaryKeyword: "ai quote follow up automation",
    audience: "Contractors, agencies and consultants whose pipeline dies in the silence after a quote goes out.",
    related: ["ai-estimate-follow-up-assistant", "ai-invoice-follow-up-automation", "ai-customer-follow-up-assistant", "ai-front-desk-assistant-contractors", "ai-operations-manager-for-contractors"],
  }),
  p({
    slug: "ai-job-lead-follow-up-assistant",
    title: "AI Job Lead Follow-Up Assistant | HireEva",
    description: "Eva follows up on every job lead within seconds, qualifies it and books it. Stop losing jobs to slow follow-up — from $1,500/month.",
    h1: "An AI job lead follow-up assistant that books while you work.",
    kicker: "JOB LEAD FOLLOW-UP",
    lede: "78% of jobs go to the first business that responds. Eva replies to every lead from your website, Google, Angi or Thumbtack in under 60 seconds, qualifies it, and books the site visit — even when you're on a job.",
    primaryKeyword: "ai job lead follow up assistant",
    audience: "Contractors, home services and local businesses competing for inbound leads where speed-to-lead decides the job.",
    related: ["ai-missed-call-follow-up-assistant", "ai-front-desk-assistant-contractors", "ai-quote-follow-up-automation", "ai-receptionist-contractors", "ai-lead-sourcing-assistant"],
  }),
  p({
    slug: "virtual-assistant-alternative-small-business",
    title: "Virtual Assistant Alternative for Small Businesses | HireEva",
    description: "Tired of training, managing and replacing virtual assistants? Eva is the AI alternative — always on, never quits, from $1,500/month plus your AI usage.",
    h1: "A virtual assistant alternative that manages itself.",
    kicker: "VA ALTERNATIVE",
    lede: "Hiring a VA means writing the SOPs, training them, time-zone juggling, and starting over when they leave. Eva is the alternative: an AI operator who already knows the job, works 24/7, and never resigns — from $1,500/month instead of $2,000–$4,000.",
    primaryKeyword: "virtual assistant alternative small business",
    audience: "Owners who've cycled through Upwork, OnlineJobs.ph or agency VAs and want something that doesn't need managing.",
    related: ["ai-virtual-assistant-small-business", "hireeva-vs-virtual-assistant", "ai-operations-manager-small-business", "ai-admin-assistant-small-business", "ai-employee-small-business", "hireeva-vs-ai-receptionist"],
  }),
];

export const SEO_SLUGS = SEO_PAGES.map((p) => p.slug);

export function getSeoPage(slug: string): SeoPage | undefined {
  return SEO_PAGES.find((p) => p.slug === slug);
}

export const SITE_ORIGIN = "https://www.hireeva.io";

export function canonicalFor(slug: string) {
  return `${SITE_ORIGIN}/${slug}.html`;
}

export function cleanUrlFor(slug: string) {
  return `${SITE_ORIGIN}/${slug}`;
}
